import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import { useState } from "react";
import { useGetTemplateContextCatalogueQuery } from "services/docGenService";

// Each section of the catalogue is a list of {name, <detail>} rows; the
// detail key differs per section and is labelled accordingly.
const SECTIONS = [
  {
    key: "roots",
    title: "Roots",
    detailKey: "description",
    label: "Description",
  },
  {
    key: "filters",
    title: "Filters",
    detailKey: "signature",
    label: "Signature",
  },
  {
    key: "globals",
    title: "Globals",
    detailKey: "signature",
    label: "Signature",
  },
  {
    key: "aliases",
    title: "Aliases",
    detailKey: "expression",
    label: "Expression",
  },
];

const matches = (row, detailKey, needle) =>
  needle === "" ||
  (row.name ?? "").toLowerCase().includes(needle) ||
  String(row[detailKey] ?? "")
    .toLowerCase()
    .includes(needle);

const CatalogueSection = ({ title, rows, detailKey, label }) => (
  <Stack gap={1}>
    <Typography variant={"h6"} component={"h3"}>
      {`${title} (${rows.length})`}
    </Typography>
    {rows.length === 0 ? (
      <Typography variant={"body2"} color={"text.secondary"}>
        {"No entries match the filter."}
      </Typography>
    ) : (
      <Table size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "35%" }}>{"Name"}</TableCell>
            <TableCell>{label}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.name}>
              <TableCell>
                <code>{row.name}</code>
              </TableCell>
              <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                {row[detailKey] ?? ""}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </Stack>
);

const TemplateCatalogueDialog = ({ open, onClose, companyId }) => {
  const [filter, setFilter] = useState("");
  const { data, isLoading, isError } = useGetTemplateContextCatalogueQuery(
    { impersonateCompanyId: companyId },
    { skip: !open }
  );

  const needle = filter.trim().toLowerCase();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={"md"}
      aria-labelledby={"template-catalogue-dialog-title"}
    >
      <DialogTitle id={"template-catalogue-dialog-title"}>
        {"Template catalogue"}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Typography variant={"body2"} color={"text.secondary"}>
            {
              "The names a template may use: root objects, custom filters, custom globals and this company's aliases."
            }
          </Typography>
          <TextField
            type={"text"}
            InputLabelProps={{ shrink: true }}
            InputProps={{ spellCheck: "false" }}
            label={"Filter"}
            autoComplete={"off"}
            variant={"filled"}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            fullWidth
          />
          {isLoading && <DefaultLoader />}
          {isError && (
            <Alert severity={"error"} variant={"outlined"}>
              {"The template catalogue could not be loaded."}
            </Alert>
          )}
          {data != null &&
            SECTIONS.map(({ key, title, detailKey, label }) => (
              <CatalogueSection
                key={key}
                title={title}
                detailKey={detailKey}
                label={label}
                rows={(data[key] ?? []).filter((row) =>
                  matches(row, detailKey, needle)
                )}
              />
            ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color={"secondary"}>
          {"Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateCatalogueDialog;
